/**
 * KeySpecsCard — wraps the brand-kit KeyFeaturesCard with a softer
 * orange→blue gradient surface (inline styles to bypass dev CSS caching).
 *
 * The brand-kit component hard-codes `bg-[#0469f0]` + `text-white`. Here we
 * inject a translucent gradient via the wrapper's inline `background` so the
 * surface reads "more faint, less saturated, more translucent" while still
 * using the production component for layout.
 */

import '../../../app/mockups/_styles/editorial.css'
import * as React from 'react'
import { KeyFeaturesCard } from '@/components/brand-kit/key-features-card'

interface KeySpecsCardProps {
  title: string
  items: string[]
}

export function KeySpecsCard({ title, items }: KeySpecsCardProps) {
  const features = items.map((text) => ({
    icon: (
      <svg viewBox="0 0 20 20" fill="currentColor" style={{ width: 16, height: 16 }} aria-hidden>
        <circle cx="10" cy="10" r="3.5" />
      </svg>
    ),
    text,
  }))

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
            .ed-keyfeatures-shell {
              position: relative;
              margin: 24px 0;
              border-radius: 12px;
              overflow: hidden;
              background: linear-gradient(135deg, rgba(255, 153, 81, 0.22) 0%, rgba(255, 153, 81, 0.10) 40%, rgba(4, 105, 240, 0.16) 100%);
              border: 1px solid rgba(4, 105, 240, 0.20);
            }
            /* Override KeyFeaturesCard's hardcoded blue surface + white text + padding */
            .ed-keyfeatures-shell .ed-keyfeatures-inner {
              background: transparent !important;
              color: #111827 !important;
              padding: 14px 18px !important;
            }
            .ed-keyfeatures-shell .ed-keyfeatures-inner h3 {
              color: #0469f0 !important;
              text-transform: uppercase;
              letter-spacing: 0.14em;
              font-size: 11px !important;
              font-weight: 800 !important;
              margin: 0 0 8px 0 !important;
            }
            .ed-keyfeatures-shell .ed-keyfeatures-inner ul {
              gap: 6px;
              display: flex;
              flex-direction: column;
              margin: 0 !important;
              padding: 0 !important;
              list-style: none !important;
            }
            .ed-keyfeatures-shell .ed-keyfeatures-inner ul li {
              display: flex !important;
              align-items: center !important;
              gap: 10px !important;
              margin: 0 !important;
              padding: 0 !important;
              font-size: 16px !important;
              line-height: 1.4 !important;
            }
            /* Suppress the .ed-prose orange ::before dot inside this card */
            .ed-keyfeatures-shell .ed-keyfeatures-inner ul li::before {
              content: none !important;
              display: none !important;
            }
            .ed-keyfeatures-shell .ed-keyfeatures-inner ul li > span:first-child {
              color: #0469f0 !important;
              width: 16px !important;
              height: 16px !important;
              flex-shrink: 0;
              display: inline-flex;
              align-items: center;
              justify-content: center;
            }
            .ed-keyfeatures-shell .ed-keyfeatures-inner ul li > span:last-child {
              color: #1f2937 !important;
              font-size: 16px !important;
              font-weight: 500 !important;
            }
          `,
        }}
      />
      <div className="ed-keyfeatures-shell">
        <KeyFeaturesCard title={title} features={features} className="ed-keyfeatures-inner" />
      </div>
    </>
  )
}
